<script setup lang="ts">
import { createOrUpdateStripeAccount, createStripePayout, getStripeBalance } from '~/api/stripe'
const { colors } = useTheme()

const user = useUser()

const stripeBalance = useStripeBalance()

async function loadBalance() {
  if (!stripeBalance.value.balance) {
    const balance = await getStripeBalance(user.value?.id)

    stripeBalance.value = {
      loading: false,
      balance,
    }
  }
}

onMounted(async () => {
  await until(user).toBeTruthy({ timeout: 2000 })

  if (user.value)
    loadBalance()
})

watch(user, async () => {
  if (!user.value) {
    stripeBalance.value = {
      loading: false,
      balance: null,
    }
  }
  else {
    loadBalance()
  }
})

const stepCount = ref(0)
const isLoadingPayout = ref(false)

const canWithdraw = computed(() => {
  if (!stripeBalance.value.balance)
    return false

  // Sum of the amount of dollars in the balance in cents
  // 1000 = $10.00
  let dollarAmount = 0

  // Converts currency to dollar
  for (const [currency, amounts] of Object.entries(stripeBalance.value.balance)) {
    if (amounts.available !== 0)
      dollarAmount += Math.round(amounts.available / STRIPE_CURRENCY_CONVERSIONS[currency])
  }

  // If they have more than $10.00 allow withdraw
  return dollarAmount >= 1000
})

const handleWithdrawClick = async () => {
  if (!canWithdraw.value || !stripeBalance.value.balance || !user.value || isLoadingPayout.value)
    return

  isLoadingPayout.value = true
  if (await createStripePayout(user.value.id)) {
    const balance = stripeBalance.value.balance

    for (const [currency, _] of Object.entries(balance))
      balance[currency].available = 0

    stripeBalance.value = {
      loading: false,
      balance,
    }
    stepCount.value = 2
  }

  isLoadingPayout.value = false
}

const loadingWithdraw = ref(false)
async function handleAccountClick() {
  loadingWithdraw.value = true
  const updateUrl = await createOrUpdateStripeAccount()
  loadingWithdraw.value = false

  if (updateUrl)
    window.location.href = updateUrl
}
</script>

<template>
  <div v-if="stepCount === 0" rounded border="~ soft-gray" relative p-2 flex="~ col" gap-2>
    <Text :color="colors.bluishDark">
      Balance
    </Text>

    <div absolute top-2 right-2>
      <ClientOnly>
        <Popper placement="bottom-end" offset-distance="10">
          <div i-mdi:help-circle-outline color-bluishSofter cursor-pointer p-2 />

          <template #content>
            <AppFloatingMenu p-2 min-w="140px" max-height="fit-content">
              <Text variant="caption" :color="colors.darkerGray">
                Amount you earned by selling your prompts on Marketplace. Funds take 7 days to be available for withdrawal. Please note that you can only withdraw amounts higher than $10. This value may vary for different currencies.
              </Text>
            </AppFloatingMenu>
          </template>
        </Popper>
      </ClientOnly>
    </div>

    <div v-if="stripeBalance.balance" flex flex-col gap-1>
      <div
        v-for="[currency, amounts] in Object.entries(stripeBalance.balance)"
        :key="currency"
        flex
        justify-between
        items-end
      >
        <Text :font-weight="900" :style="{ lineHeight: '1em' }">
          {{ formatCurrency(amounts.available / 100, currency) }}
        </Text>
        <div flex items-center>
          <div i-ic:baseline-hourglass-bottom text-bluish-dark w="12px" h="12px" />
          <Text variant="small" :color="colors.bluishDark">
            {{ formatCurrency(amounts.pending / 100, currency) }}
          </Text>
        </div>
      </div>
    </div>
    <AppLoading v-else />

    <AppLink v-if="canWithdraw" show-arrow cursor-pointer @click="() => stepCount = 1">
      <Text variant="small" bold :color="colors.darkerGray">
        {{ isLoadingPayout ? 'Loading' : 'Withdraw' }}
      </Text>
    </AppLink>
  </div>
  <div v-if="stepCount === 1" rounded border="~ soft-gray" p-3 flex="~ col" gap-2>
    <template v-if="!isLoadingPayout">
      <div flex flex-col gap-1>
        <Text variant="small" :color="colors.darkerGray">
          The funds will be sent where you chose on your payment information.
        </Text>

        <AppLink show-arrow cursor-pointer @click="handleAccountClick">
          <Text variant="small" bold :color="colors.darkerGray">
            Change Payment Information
          </Text>
        </AppLink>
      </div>
      <div flex justify-end gap-2>
        <AppLink cursor-pointer @click="() => stepCount = 0">
          <Text variant="small" bold :color="colors.orange">
            Cancel
          </Text>
        </AppLink>
        <AppLink cursor-pointer @click="handleWithdrawClick">
          <Text variant="small" bold :color="colors.darkerGray">
            Confirm
          </Text>
        </AppLink>
      </div>
    </template>
    <div v-else flex center w-full h="136px">
      <div animate-spin :style="{ animationDuration: '2s' }">
        <IconSpinner :size="20" :color="colors.darkerGray" />
      </div>
    </div>
  </div>
  <div v-if="stepCount === 2" rounded border="~ soft-gray" p-3 flex="~ col" gap-2>
    <div v-if="loadingWithdraw" flex center w-full h="136px">
      <div animate-spin :style="{ animationDuration: '2s' }">
        <IconSpinner :size="20" :color="colors.darkerGray" />
      </div>
    </div>
    <div v-else>
      <Text variant="small" :color="colors.darkerGray">
        Success! The withdraw may take some time to be processed
      </Text>
      <AppLink cursor-pointer flex justify-end @click="() => stepCount = 0">
        <Text variant="small" bold :color="colors.darkerGray">
          Dismiss
        </Text>
      </AppLink>
    </div>
  </div>
</template>
